import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { Amplify } from 'aws-amplify';
import { Hub } from 'aws-amplify/utils';
import { 
  signInWithRedirect, 
  signOut, 
  getCurrentUser, 
  fetchAuthSession
} from 'aws-amplify/auth';
import { authConfig, REQUIRED_GROUP, AZURE_PROVIDER } from '../config/auth-config';

// Check if auth bypass is enabled
const isBypassEnabled = () => {
  return process.env.REACT_APP_BYPASS_AUTH === 'true';
};

// Check if configuration is complete
const isConfigComplete = () => {
  const userPoolId = process.env.REACT_APP_COGNITO_USER_POOL_ID;
  const clientId = process.env.REACT_APP_COGNITO_CLIENT_ID;
  const domain = process.env.REACT_APP_COGNITO_DOMAIN;
  
  return userPoolId && 
         clientId && 
         domain && 
         !userPoolId.includes('REPLACE_WITH') &&
         !clientId.includes('REPLACE_WITH') &&
         !domain.includes('REPLACE_WITH');
};

// Only configure Amplify if config is complete and bypass is not enabled
if (!isBypassEnabled() && isConfigComplete()) {
  Amplify.configure(authConfig);
}

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userGroups, setUserGroups] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [error, setError] = useState(null);
  const [configError, setConfigError] = useState(null);

  // Check configuration on mount
  useEffect(() => {
    // If bypass is enabled, set mock user and skip auth
    if (isBypassEnabled()) {
      setUser({
        username: 'local-dev-user',
        email: 'dev@localhost',
        name: 'Local Development User',
      });
      setUserGroups(['Admins', 'SuperAdmin']);
      setIsAuthorized(true);
      setIsLoading(false);
      return;
    }

    if (!isConfigComplete()) {
      setConfigError('Cognito configuration is incomplete. Please update your .env file.');
      setIsLoading(false);
      return;
    }
  }, []);

  // Check if user has required group
  const checkAuthorization = useCallback((groups) => {
    const hasAccess = groups.includes(REQUIRED_GROUP) || groups.includes('SuperAdmin') || groups.includes('Admins');
    setIsAuthorized(hasAccess);
    return hasAccess;
  }, []);

  // Get user info and groups from session
  const loadUserSession = useCallback(async (forceRefresh = false) => {
    if (!isConfigComplete()) return;
    
    try {
      setIsLoading(true);
      setError(null);

      const currentUser = await getCurrentUser();
      
      // Force refresh token to get updated groups
      const session = await fetchAuthSession({ forceRefresh });
      
      // Extract groups from ID token
      const idToken = session.tokens?.idToken;
      let groups = idToken?.payload['cognito:groups'] || [];
      
      // If no groups on first load, try refreshing the token once
      if (groups.length === 0 && !forceRefresh) {
        const refreshedSession = await fetchAuthSession({ forceRefresh: true });
        const refreshedIdToken = refreshedSession.tokens?.idToken;
        groups = refreshedIdToken?.payload['cognito:groups'] || [];
      }

      setUser({
        username: currentUser.username,
        email: idToken?.payload?.email || currentUser.username,
        name: idToken?.payload?.name || '',
      });
      setUserGroups(groups);
      checkAuthorization(groups);

    } catch (err) {
      setUser(null);
      setUserGroups([]);
      setIsAuthorized(false);
    } finally {
      setIsLoading(false);
    }
  }, [checkAuthorization]);

  // Listen for auth events
  useEffect(() => {
    if (isBypassEnabled() || !isConfigComplete()) return;
    
    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      switch (payload.event) {
        case 'signedIn':
          loadUserSession();
          break;
        case 'signedOut':
          setUser(null);
          setUserGroups([]);
          setIsAuthorized(false);
          break;
        case 'tokenRefresh':
          loadUserSession();
          break;
        case 'signInWithRedirect':
          loadUserSession();
          break;
        case 'signInWithRedirect_failure':
          setError('Sign in failed. Please try again.');
          setIsLoading(false);
          break;
        default:
          break;
      }
    });

    // Check for existing session on mount
    loadUserSession();

    return () => unsubscribe();
  }, [loadUserSession]);

  // Sign in with Azure AD
  const login = async () => {
    if (isBypassEnabled()) {
      // Already logged in with bypass
      return;
    }
    
    if (!isConfigComplete()) {
      setError('Cognito configuration is incomplete');
      return;
    }
    
    try {
      setError(null);
      await signInWithRedirect({ provider: { custom: AZURE_PROVIDER } });
    } catch (err) {
      console.error('Login error:', err);
      setError(err.message || 'Failed to initiate login');
    }
  };

  // Sign out
  const logout = async () => {
    if (isBypassEnabled()) {
      // Can't logout in bypass mode
      return;
    }
    
    try {
      await signOut({ global: true });
      localStorage.removeItem('currentPage');
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  const value = {
    user,
    userGroups,
    isLoading,
    isAuthenticated: !!user,
    isAuthorized,
    error,
    configError,
    login,
    logout,
    requiredGroup: REQUIRED_GROUP,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
